/** UK-style date/time formatting used across the Engage platform UI. */

const LOCALE = 'en-GB';
const TZ = undefined; // Browser local timezone

export function parseDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** e.g. 15 May 2026, 14:32 */
export function formatDateTime(value: string | Date | null | undefined): string {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  });
}

/** e.g. 15 May 2026 */
export function formatDate(value: string | Date | null | undefined): string {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: TZ,
  });
}

/** e.g. 14:32 */
export function formatTime(value: string | Date | null | undefined): string {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  });
}

/** Short range for campaign rows: 1 Jan 2026 – 31 Mar 2026 */
export function formatDateRange(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
): string {
  const a = formatDate(start);
  const b = formatDate(end);
  if (a === '—' && b === '—') return '—';
  if (a === '—') return b;
  if (b === '—') return a;
  return `${a} – ${b}`;
}

/** Relative label for activity feeds; falls back to full date/time after 14 days. */
export function formatRelativeDateTime(value: string | Date | null | undefined): string {
  const d = parseDate(value);
  if (!d) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 14) return `${days}d ago`;
  return formatDateTime(d);
}

/** Current moment for platform header clock */
export function formatNowDateTime(date: Date = new Date()): string {
  return formatDateTime(date);
}
